/**
 * Station alerts in words (the station workspaces plan, step 7; lib/api/station-alerts): an alert's level and state,
 * how long it has been out, its value against the window and the stop limit, who acknowledged it and how, and the
 * acknowledgement as it is written (one tap on the floor: a preset, with words needed only for "other"). Pure.
 */
import {
  ALERT_SETTINGS_MAX,
  ALERT_SETTINGS_MIN,
  type StationAlert,
  type StationAlertAck,
  type StationAlertAckKind,
} from "@/lib/api/station-alerts";
import { errorCodeOf, errorDetailOf, statusOf } from "@/lib/api/error";
import { bandText, momentText, valueText, type LevelLook } from "./station-board";

/** An alert's level in words, with its badge's tone: a stop the hardest; the side it is out on where the server says. */
export function alertLevelLook(a: Pick<StationAlert, "level" | "side">): LevelLook {
  if (a.level === "stop") {
    return { word: a.side ? `Stop: ${a.side} the stop limit` : "Stop", tone: "danger" };
  }
  return { word: a.side ? `${a.side === "above" ? "Above" : "Below"} the window` : "Outside the window", tone: "danger" };
}

/**
 * Where an alert stands, in words: open, stale (no live value since a time on the plant's clock), acknowledged, or
 * cleared and why. now is the moment the time is told against (its day is left out when it is today's).
 */
export function alertStateText(
  a: Pick<StationAlert, "state" | "stale_since" | "cleared_reason" | "acked_at">,
  zone?: string,
  now: string = new Date().toISOString(),
): string {
  if (a.state === "cleared") return a.cleared_reason === "unwatched" ? "Cleared: no longer watched here" : "Cleared: back inside";
  const stale = a.stale_since ? ` (no live value since ${momentText(a.stale_since, now, zone)})` : "";
  return a.state === "acknowledged" ? `Acknowledged${stale}` : `Open${stale}`;
}

/**
 * How far an alert stepped up unacknowledged, in words, and whether that still stands (urgent): open, it is still
 * waiting for someone; acknowledged, it is past, said as such. Null when it has not stepped up, or once cleared.
 */
export function escalationText(a: Pick<StationAlert, "escalation" | "state">): { text: string; urgent: boolean } | null {
  if (a.escalation < 1 || a.state === "cleared") return null;
  const whom = a.escalation >= 2 ? "the org's admins" : "the station's leads";
  if (a.state === "acknowledged") return { text: `Stepped up to ${whom} before it was acknowledged.`, urgent: false };
  const time = a.escalation >= 2 ? "twice the plant's escalation time" : "the plant's escalation time";
  return { text: `Stepped up to ${whom}: not acknowledged in ${time}.`, urgent: true };
}

/** How long an alert has been out (or was, once cleared): "8 min", "2 h 5 min", "3 days". */
export function outForText(min: number): string {
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) {
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest ? `${h} h ${rest} min` : `${h} h`;
  }
  return `${Math.floor(m / (24 * 60))} days`;
}

/** An alert's value and the band it is judged against, in its own unit. */
export function alertValueLine(a: Pick<StationAlert, "value" | "unit" | "band">): string {
  const value = valueText({ value: a.value, unit: a.unit, band: a.band });
  const band = bandText({ band: a.band, unit: a.unit });
  return band ? `${value}, against ${band}` : value;
}

/**
 * The one-tap acknowledgements, in the order the floor uses them, worded as the server stores them (ackNoteOf), so the
 * button and the note it leaves read the same.
 */
export const ACK_PRESETS: readonly { kind: StationAlertAckKind; label: string }[] = [
  { kind: "changed", label: "Changed something" },
  { kind: "checked_ok", label: "Checked, nothing wrong" },
  { kind: "reading_wrong", label: "The reading is wrong" },
  { kind: "maintenance", label: "Called maintenance" },
  { kind: "other", label: "Other" },
];

/**
 * What an acknowledgement says of itself on the list: its preset's words, and the detail written with it. On an alert
 * open again (reopened, or risen to stop since), it was an earlier one: the alert takes a new acknowledgement.
 */
export function ackText(a: Pick<StationAlert, "state" | "ack_kind" | "ack_note" | "acked_by_name">): string | null {
  if (!a.ack_kind && !a.ack_note) return null;
  const who = a.acked_by_name ? ` by ${a.acked_by_name}` : "";
  const said = `${who}${a.ack_note ? `: ${a.ack_note}` : ""}`;
  return a.state === "open" ? `Acknowledged earlier${said}. It is open again since.` : `Acknowledged${said}`;
}

/**
 * Whether an alert takes an acknowledgement now, as the server does: an open one, including one acknowledged before it
 * reopened or rose to stop (acked_at is then the earlier one's), and a cleared one never acknowledged (it keeps its state
 * and takes the words). One acknowledged, or cleared after its acknowledgement, is done.
 */
export function alertAckable(a: Pick<StationAlert, "state" | "acked_at">): boolean {
  return a.state === "open" || (a.state === "cleared" && !a.acked_at);
}

/** Whether an acknowledgement goes on the Actions list as a change tried, for its effect to be followed. */
export function ackTried(kind: StationAlertAckKind): boolean {
  return kind === "changed" || kind === "maintenance";
}

/** The most an acknowledgement's detail may say, as the server allows. */
export const ACK_DETAIL_MAX = 1800;

/** What stops an acknowledgement being sent, in words; null when it may be. */
export function ackProblem(kind: StationAlertAckKind | "", detail: string): string | null {
  if (!kind) return "Choose what was done.";
  if (kind === "other" && !detail.trim()) return "Say what was done.";
  if (detail.trim().length > ACK_DETAIL_MAX) return `The detail is longer than ${ACK_DETAIL_MAX} characters.`;
  return null;
}

/** An acknowledgement as the server takes it: the detail trimmed, and left out when empty. */
export function ackBody(kind: StationAlertAckKind, detail: string): StationAlertAck {
  const d = detail.trim();
  return d ? { kind, detail: d } : { kind };
}

/** Why an acknowledgement failed, at the form: who may, what the server needs, never a 5xx's raw detail. */
export function ackErrorWords(err: unknown): string {
  const code = errorCodeOf(err);
  const status = statusOf(err);
  if (code === "not_station_member") return "Only a member of this station, or an org admin, acknowledges its alerts.";
  if (code === "ack_detail_required") return "Say what was done.";
  if (code === "alert_not_found") return "This alert is no longer there. The list is read again.";
  if (status !== undefined && status >= 500) {
    return "The platform failed while saving. It may have gone through: the list is read again, so check before trying again.";
  }
  return errorDetailOf(err) ?? (status === undefined ? "The platform did not answer. Try again." : `It could not be saved (HTTP ${status}). Try again.`);
}

/** The header's Alarms button, named with the alerts at the person's stations not yet acknowledged. */
export function alarmsLabel(unacked: number): string {
  return unacked > 0
    ? `Alarms, ${unacked} ${unacked === 1 ? "alert" : "alerts"} at your stations not yet acknowledged`
    : "Alarms";
}

// ── The org's alert settings, in words ──

/** Whom an unacknowledged alert steps up to, and when, at the org's escalation minutes. */
export function escalationTiersText(minutes: number): string {
  return (
    `An open alert not acknowledged within ${minutes} minutes steps up to the station's leads (else its engineers, ` +
    `else the org's admins), and after ${minutes * 2} minutes to the org's admins. An acknowledgement stops it.`
  );
}

/** Whether, and how, the platform emails station alerts. */
export function alertEmailText(enabled: boolean): string {
  return enabled
    ? "Station alerts are emailed. A warn goes to the station's operators (else its leads, its engineers, the org's " +
        "admins), at most every 10 minutes a person; a stop, or a rise to stop, to its operators and leads at once; a " +
        "step up, to whom it reaches."
    : "The platform does not email station alerts yet. They are listed on the Alarms page and on each station, and " +
        "they step up there all the same.";
}

/** What stops the escalation minutes being saved, in words; null when they may be. */
export function escalationProblem(text: string): string | null {
  // Digits only: Number() would also take "1e1" or "0x10".
  if (!/^\d+$/.test(text.trim())) return "Give a whole number of minutes.";
  const v = Number(text.trim());
  if (v < ALERT_SETTINGS_MIN || v > ALERT_SETTINGS_MAX) {
    return `Give between ${ALERT_SETTINGS_MIN} and ${ALERT_SETTINGS_MAX} minutes.`;
  }
  return null;
}

/** Why the settings failed to save, in words; never a 5xx's raw detail. */
export function alertSettingsErrorWords(err: unknown): string {
  const code = errorCodeOf(err);
  const status = statusOf(err);
  if (code === "invalid_escalation") return `Give between ${ALERT_SETTINGS_MIN} and ${ALERT_SETTINGS_MAX} minutes.`;
  if (code === "insufficient_permission" || status === 403) return "Only an org admin changes this.";
  if (status !== undefined && status >= 500) {
    return "The platform failed while saving. Read the page again before trying again.";
  }
  return (
    errorDetailOf(err) ??
    (status === undefined ? "The platform did not answer. Try again." : `It could not be saved (HTTP ${status}). Try again.`)
  );
}
